import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  FilePreviewModal,
  FilterContext,
  PageViewContext,
  PageViewTypeEnum,
  PaginationContext,
  SearchContext,
  SortContext,
  Spinner,
} from "dash-ui";

import { BulkActionsContext } from "common/contexts/bulkActionsContext/bulkActionsContext";
import {
  useGetMediaFoldersAndFilesQuery,
  useObserveMediaChangesSubscription,
} from "gql/generated.graphql";

import MediaFilesList from "./components/MediaFilesList/MediaFilesList";
import MediaFilesTable from "./components/MediaFilesTable/MediaFilesTable";

const MediaFilesView = ({
  propertyId,
  folderId,
  isSlideshowFolder,
}: {
  propertyId: string;
  folderId?: string;
  isSlideshowFolder?: boolean;
}) => {
  const {
    setAllSelectedTotal,
    setFilter: setFilterForBulkActionsContext,
    isAllSelected,
    selectMedia,
  } = useContext(BulkActionsContext);
  const { state: filterState } = useContext(FilterContext);
  const { state: sort } = useContext(SortContext);
  const { searchSlug } = useContext(SearchContext);
  const { viewType } = useContext(PageViewContext);
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const [fileToPreviewId, setFileToPreviewId] = useState<string | null>(null);
  const filter = useMemo(
    () => ({
      propertyId,
      ...(folderId && { mediaFolderId: folderId }),
      fileTypes: filterState.fileTypes || [],
      searchText: searchSlug.length > 2 ? searchSlug : null,
      ...(filterState.brandingType && {
        brandingType: filterState.brandingType,
      }),
    }),
    [filterState, searchSlug, folderId, propertyId]
  );

  const { data, refetch, loading } = useGetMediaFoldersAndFilesQuery({
    variables: {
      filter,
      pagination: { skip, limit },
      ...(sort?.fieldName && { sort: [sort] }),
    },
    fetchPolicy: "network-only",
  });

  useObserveMediaChangesSubscription({
    variables: { filter },
    onData() {
      refetch();
    },
  });

  const { media, total } = useMemo(
    () => ({
      media: {
        folders: data?.getMediaFoldersAndFiles.foldersCollection || [],
        files: data?.getMediaFoldersAndFiles.filesCollection || [],
      },
      total: {
        foldersTotal: data?.getMediaFoldersAndFiles.foldersTotalCount || 0,
        filesTotal: data?.getMediaFoldersAndFiles.filesTotalCount || 0,
      },
    }),
    [data]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total.foldersTotal + total.filesTotal);
    setAllSelectedTotal(total.filesTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    setFilterForBulkActionsContext(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (isAllSelected) {
      selectMedia(media.files.map((p) => ({ id: p._id, value: true })));
    }
  }, [isAllSelected, media, selectMedia]);
  return (
    <Box pos="relative" w="100%">
      {!!fileToPreviewId && (
        <FilePreviewModal
          isOpened
          //isPortfolioView={true} not ideal, but works for now to bypass access check
          isPortfolioView={true}
          onClose={() => setFileToPreviewId(null)}
          defaultIndex={media.files.findIndex(
            (item) => item._id === fileToPreviewId
          )}
          files={media.files.map((item) => {
            const [file] = item.files;

            return {
              _id: item._id,
              type: file.type,
              name: file.name,
              publicUrl: file.publicUrl,
              s3Key: file.s3Key,
            };
          })}
        />
      )}
      {viewType === PageViewTypeEnum.CARDS ? (
        <MediaFilesList
          setFileToPreviewId={setFileToPreviewId}
          propertyId={propertyId}
          folderId={folderId}
          media={media}
          total={total}
          isSlideshowFolder={isSlideshowFolder}
        />
      ) : (
        <MediaFilesTable
          media={media}
          propertyId={propertyId}
          setFileToPreviewId={setFileToPreviewId}
        />
      )}

      {loading && <Spinner fullContent />}
    </Box>
  );
};

export default MediaFilesView;
